import type {
  Service,
  Project,
  Review,
  BlogPost,
  PricingTier,
  FAQItem,
  ProcessStep,
} from '@/lib/index';
import { IMAGES } from '@/assets/images';

export const services: Service[] = [
  {
    id: 'flooring',
    icon: 'Layers',
    name: 'Hardwood & Luxury Vinyl Flooring',
    description: 'Premium hardwood and luxury vinyl plank installation with expert craftsmanship. Transform your space with durable, beautiful flooring that lasts generations.',
    startingPrice: 2500,
    features: [
      'Premium hardwood species selection',
      'Luxury vinyl plank (LVP) options',
      'Professional subfloor preparation',
      'Precision installation techniques',
      'Custom staining and finishing',
      'Lifetime craftsmanship warranty',
    ],
    howWeWork: 'Our flooring specialists begin with a comprehensive site assessment, measuring every detail to ensure perfect material calculations. We prepare subfloors to industry standards, then install your chosen flooring with meticulous attention to pattern alignment and expansion gaps. Every plank is inspected before installation.',
    warranty: '10-year installation warranty on all flooring projects. Lifetime craftsmanship guarantee on hardwood installations.',
    image: IMAGES.FLOORING_1,
  },
  {
    id: 'cabinets',
    icon: 'Box',
    name: 'Custom Cabinet Installation',
    description: 'Bespoke kitchen and bathroom cabinetry designed to maximize storage and elevate your home\'s aesthetic. Built to last with premium materials.',
    startingPrice: 3500,
    features: [
      'Custom cabinet design consultation',
      'Premium wood and hardware selection',
      'Soft-close drawer systems',
      'Precision installation and leveling',
      'Crown molding and trim work',
      '5-year hardware warranty',
    ],
    howWeWork: 'We start with 3D design renderings to visualize your new cabinetry. Our team handles all demolition, plumbing adjustments, and electrical work. Cabinets are installed with laser-level precision, ensuring perfect alignment and functionality. Final touches include hardware installation and detailed quality checks.',
    warranty: '5-year warranty on all cabinet installations and hardware. Lifetime support for adjustments.',
    image: IMAGES.CABINETS_1,
  },
  {
    id: 'interior',
    icon: 'Palette',
    name: 'Interior Design & Upgrade',
    description: 'Complete interior transformations including paint, lighting, fixtures, and decor. Elevate your living spaces with cohesive, professional design.',
    startingPrice: 5000,
    features: [
      'Full interior design consultation',
      'Color palette and material selection',
      'Lighting design and installation',
      'Fixture and hardware upgrades',
      'Custom millwork and trim',
      'Project management and coordination',
    ],
    howWeWork: 'Our interior designers create a comprehensive design plan tailored to your lifestyle and aesthetic preferences. We coordinate all trades—painters, electricians, carpenters—to execute the vision seamlessly. Every detail is curated, from paint sheens to switch plate finishes.',
    warranty: '2-year warranty on all interior upgrades and installations. Complimentary touch-up service within first year.',
    image: IMAGES.INTERIOR_1,
  },
  {
    id: 'renovation',
    icon: 'Home',
    name: 'Full Room Renovation',
    description: 'Complete room transformations from concept to completion. Kitchen, bathroom, basement, or whole-home renovations executed with precision.',
    startingPrice: 15000,
    features: [
      'Architectural design and planning',
      'Permit acquisition and management',
      'Structural modifications',
      'Plumbing and electrical upgrades',
      'Complete finish installation',
      'Post-completion walkthrough',
    ],
    howWeWork: 'Full renovations begin with architectural drawings and permit applications. Our project managers coordinate all trades, ensuring work progresses on schedule. We handle demolition, framing, mechanical systems, and all finish work. Daily site photos keep you informed throughout the process.',
    warranty: '5-year comprehensive warranty on all structural and finish work. 1-year warranty on mechanical systems.',
    image: IMAGES.RENOVATION_1,
  },
];

export const portfolioProjects: Project[] = [
  {
    id: 'project-1',
    title: 'Modern Kitchen Transformation',
    location: 'Vancouver',
    province: 'BC',
    budget: { min: 35000, max: 42000 },
    duration: '6 weeks',
    category: 'cabinets',
    style: 'Contemporary',
    coverImage: IMAGES.CABINETS_7,
    images: [IMAGES.CABINETS_7, IMAGES.CABINETS_2, IMAGES.CABINETS_3],
    scopeOfWork: [
      'Custom white oak cabinetry with soft-close drawers',
      'Quartz waterfall island countertop',
      'Integrated appliance panels',
      'Under-cabinet LED lighting',
      'Brass hardware and fixtures',
    ],
    challenge: 'The existing kitchen had low ceilings and poor natural light, making the space feel cramped and dated.',
    solution: 'We installed floor-to-ceiling cabinetry to draw the eye upward, added recessed lighting, and used light-colored materials to maximize brightness. The result is an airy, modern kitchen that feels twice its actual size.',
  },
  {
    id: 'project-2',
    title: 'Luxury Vinyl Plank Installation',
    location: 'Toronto',
    province: 'ON',
    budget: { min: 8500, max: 11000 },
    duration: '2 weeks',
    category: 'flooring',
    style: 'Transitional',
    coverImage: IMAGES.VINYL_FLOOR_2,
    images: [IMAGES.VINYL_FLOOR_2, IMAGES.VINYL_FLOOR_9, IMAGES.FLOORING_6],
    scopeOfWork: [
      '1,200 sq ft luxury vinyl plank flooring',
      'Subfloor leveling and moisture barrier',
      'Herringbone pattern installation',
      'Custom transition strips',
      'Baseboard replacement',
    ],
    challenge: 'The client wanted the look of hardwood but needed waterproof flooring for a home with pets and children.',
    solution: 'We selected premium LVP with realistic wood grain texture and installed it in a sophisticated herringbone pattern. The result combines durability with timeless elegance.',
  },
  {
    id: 'project-3',
    title: 'Open-Concept Living Room Redesign',
    location: 'Calgary',
    province: 'AB',
    budget: { min: 18000, max: 23000 },
    duration: '4 weeks',
    category: 'interior',
    style: 'Modern Farmhouse',
    coverImage: IMAGES.INTERIOR_6,
    images: [IMAGES.INTERIOR_6, IMAGES.INTERIOR_3, IMAGES.INTERIOR_7],
    scopeOfWork: [
      'Removal of load-bearing wall with beam installation',
      'Shiplap accent wall',
      'Recessed and pendant lighting',
      'Built-in shelving units',
      'Custom paint and trim work',
    ],
    challenge: 'The living and dining rooms were separated by a wall, creating two small, disconnected spaces.',
    solution: 'We removed the wall and installed a structural beam, creating a spacious open-concept area. Custom built-ins and strategic lighting define zones while maintaining flow.',
  },
  {
    id: 'project-4',
    title: 'Master Bathroom Spa Retreat',
    location: 'Montreal',
    province: 'QC',
    budget: { min: 28000, max: 35000 },
    duration: '5 weeks',
    category: 'renovation',
    style: 'Luxury Spa',
    coverImage: IMAGES.BATHROOM_3,
    images: [IMAGES.BATHROOM_3, IMAGES.BATHROOM_1, IMAGES.BATHROOM_10],
    scopeOfWork: [
      'Complete bathroom gut and rebuild',
      'Heated tile flooring',
      'Walk-in shower with rain head',
      'Freestanding soaking tub',
      'Double vanity with quartz countertops',
    ],
    challenge: 'The outdated bathroom had poor ventilation and a cramped layout that wasted space.',
    solution: 'We reconfigured the layout to maximize space, installed a powerful ventilation system, and used large-format tiles to create a seamless, spa-like atmosphere.',
  },
  {
    id: 'project-5',
    title: 'Hardwood Floor Restoration',
    location: 'Ottawa',
    province: 'ON',
    budget: { min: 6500, max: 8500 },
    duration: '1 week',
    category: 'flooring',
    style: 'Classic',
    coverImage: IMAGES.FLOORING_7,
    images: [IMAGES.FLOORING_7, IMAGES.FLOORING_8, IMAGES.FLOORING_3],
    scopeOfWork: [
      'Sanding and refinishing of 900 sq ft oak floors',
      'Custom stain color matching',
      'Three coats of polyurethane finish',
      'Repair of damaged boards',
      'Baseboard refinishing',
    ],
    challenge: 'The 50-year-old hardwood floors were heavily scratched and had uneven staining from previous DIY attempts.',
    solution: 'We sanded down to bare wood, repaired damaged sections, and applied a custom-mixed stain to achieve uniform color. The floors now look brand new.',
  },
  {
    id: 'project-6',
    title: 'Contemporary Kitchen Remodel',
    location: 'Edmonton',
    province: 'AB',
    budget: { min: 42000, max: 50000 },
    duration: '8 weeks',
    category: 'cabinets',
    style: 'Ultra-Modern',
    coverImage: IMAGES.CABINETS_10,
    images: [IMAGES.CABINETS_10, IMAGES.CABINETS_4, IMAGES.CABINETS_5],
    scopeOfWork: [
      'Handleless slab-door cabinetry',
      'Integrated refrigerator and dishwasher',
      'Porcelain slab backsplash',
      'Smart home lighting system',
      'Custom pantry organization',
    ],
    challenge: 'The client wanted a minimalist aesthetic without sacrificing storage or functionality.',
    solution: 'We designed a sleek handleless system with push-to-open mechanisms and maximized storage with custom pull-out organizers and a floor-to-ceiling pantry.',
  },
  {
    id: 'project-7',
    title: 'Basement Suite Conversion',
    location: 'Winnipeg',
    province: 'MB',
    budget: { min: 45000, max: 55000 },
    duration: '10 weeks',
    category: 'renovation',
    style: 'Modern',
    coverImage: IMAGES.BATHROOM_7,
    images: [IMAGES.BATHROOM_7, IMAGES.BATHROOM_4, IMAGES.RENOVATION_9],
    scopeOfWork: [
      'Full basement framing and insulation',
      'Bedroom, bathroom, and kitchenette',
      'Separate entrance installation',
      'Plumbing and electrical rough-in',
      'Complete finish package',
    ],
    challenge: 'The unfinished basement had low ceilings and moisture issues that needed addressing before conversion.',
    solution: 'We installed a sump pump system, waterproofed walls, and used strategic design to make the 7-foot ceilings feel spacious. The suite now generates rental income.',
  },
  {
    id: 'project-8',
    title: 'Dining Room Elegance Upgrade',
    location: 'Vancouver',
    province: 'BC',
    budget: { min: 12000, max: 16000 },
    duration: '3 weeks',
    category: 'interior',
    style: 'Traditional',
    coverImage: IMAGES.DINING_ROOM_9,
    images: [IMAGES.DINING_ROOM_9, IMAGES.DINING_ROOM_4, IMAGES.INTERIOR_8],
    scopeOfWork: [
      'Wainscoting and crown molding installation',
      'Custom chandelier and dimmer system',
      'Venetian plaster accent wall',
      'Hardwood floor refinishing',
      'Window treatment installation',
    ],
    challenge: 'The dining room felt plain and lacked architectural interest despite good bones.',
    solution: 'We added classic millwork details and a textured accent wall to create depth. The new lighting system sets the perfect ambiance for entertaining.',
  },
  {
    id: 'project-9',
    title: 'Whole-Home Flooring Replacement',
    location: 'Toronto',
    province: 'ON',
    budget: { min: 22000, max: 28000 },
    duration: '4 weeks',
    category: 'flooring',
    style: 'Contemporary',
    coverImage: IMAGES.VINYL_FLOOR_10,
    images: [IMAGES.VINYL_FLOOR_10, IMAGES.FLOORING_5, IMAGES.FLOORING_2],
    scopeOfWork: [
      '2,400 sq ft engineered hardwood installation',
      'Stair tread and riser replacement',
      'Transition strips between rooms',
      'Baseboard and quarter-round installation',
      'Furniture moving and protection',
    ],
    challenge: 'The home had a mix of carpet, laminate, and tile that created visual chaos and made spaces feel disjointed.',
    solution: 'We installed consistent engineered hardwood throughout, creating flow and cohesion. The unified flooring makes the home feel larger and more valuable.',
  },
];

export const reviews: Review[] = [
  {
    id: 'review-1',
    name: 'Sarah Chen',
    location: 'Vancouver',
    province: 'BC',
    rating: 5,
    text: 'Vancore transformed our dated kitchen into a modern masterpiece. The team was professional, punctual, and incredibly detail-oriented. They stayed within budget and timeline, which is rare in renovations. Highly recommend!',
    date: '2026-03-15',
  },
  {
    id: 'review-2',
    name: 'Michael Thompson',
    location: 'Toronto',
    province: 'ON',
    rating: 5,
    text: 'We hired Vancore for luxury vinyl flooring installation, and the results exceeded our expectations. The herringbone pattern looks stunning, and the quality of workmanship is evident in every detail. Worth every penny.',
    date: '2026-02-28',
  },
  {
    id: 'review-3',
    name: 'Emily Dubois',
    location: 'Montreal',
    province: 'QC',
    rating: 5,
    text: 'Our master bathroom renovation was a dream come true. Vancore handled everything from permits to final touches. The spa-like atmosphere they created is exactly what we envisioned. Exceptional service from start to finish.',
    date: '2026-01-20',
  },
  {
    id: 'review-4',
    name: 'David Patel',
    location: 'Calgary',
    province: 'AB',
    rating: 5,
    text: 'The open-concept renovation completely changed how we use our home. Vancore\'s design expertise and construction quality are top-notch. They communicated clearly throughout the project and delivered exactly what they promised.',
    date: '2025-12-10',
  },
  {
    id: 'review-5',
    name: 'Jennifer Wilson',
    location: 'Ottawa',
    province: 'ON',
    rating: 5,
    text: 'Our 50-year-old hardwood floors look brand new thanks to Vancore. The refinishing work is flawless, and they matched the stain color perfectly. The team was respectful of our home and cleaned up thoroughly each day.',
    date: '2025-11-05',
  },
  {
    id: 'review-6',
    name: 'Robert Nguyen',
    location: 'Winnipeg',
    province: 'MB',
    rating: 5,
    text: 'Converting our basement into a rental suite was a big investment, and Vancore made it stress-free. They handled all permits, addressed moisture issues, and created a beautiful, functional space. The suite is now generating income!',
    date: '2025-10-18',
  },
];

export const pricingTiers: PricingTier[] = [
  {
    id: 'essential',
    name: 'Essential',
    priceRange: { min: 2500, max: 10000 },
    features: [
      'Single-room projects',
      'Flooring installation or cabinet refresh',
      'Standard materials and finishes',
      'Basic design consultation',
      '1-year warranty',
      'Project timeline: 1-3 weeks',
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    priceRange: { min: 10000, max: 25000 },
    features: [
      'Multi-room renovations',
      'Custom cabinetry and millwork',
      'Premium materials and fixtures',
      'Full design service included',
      '3-year warranty',
      'Project timeline: 3-6 weeks',
    ],
    highlighted: true,
  },
  {
    id: 'signature',
    name: 'Signature',
    priceRange: { min: 25000, max: 50000 },
    features: [
      'Whole-home transformations',
      'Luxury materials and finishes',
      'Architectural design services',
      'Dedicated project manager',
      '5-year comprehensive warranty',
      'Project timeline: 6-12 weeks',
    ],
  },
];

export const faqItems: FAQItem[] = [
  {
    id: 'faq-1',
    question: 'How long does a typical renovation project take?',
    answer: 'Project timelines vary based on scope. Single-room projects like flooring installation typically take 1-3 weeks. Kitchen and bathroom renovations average 4-8 weeks. Whole-home renovations can take 8-16 weeks. We provide detailed timelines during the consultation phase and keep you updated throughout the process.',
  },
  {
    id: 'faq-2',
    question: 'Do you serve all of Canada?',
    answer: 'Yes! While we\'re headquartered in Burnaby, BC, we serve major cities across Canada including Vancouver, Toronto, Calgary, Montreal, Ottawa, Edmonton, and Winnipeg. Our teams are strategically located to provide the same high-quality service nationwide.',
  },
  {
    id: 'faq-3',
    question: 'Are you licensed and insured?',
    answer: 'Absolutely. Vancore Building Solutions Inc. is fully licensed, bonded, and insured. We carry comprehensive liability insurance and workers\' compensation coverage. All our contractors are certified professionals with years of experience in their respective trades.',
  },
  {
    id: 'faq-4',
    question: 'What payment methods do you accept?',
    answer: 'We accept bank transfers, certified checks, and major credit cards. Payment is typically structured in milestones: 30% deposit to secure your project date, 40% at project midpoint, and 30% upon completion and your final approval. We never require full payment upfront.',
  },
  {
    id: 'faq-5',
    question: 'Do I need to be home during the renovation?',
    answer: 'Not necessarily. Many clients prefer to be present, but it\'s not required. We can work with your schedule and provide daily photo updates. For security, we use lockboxes and maintain strict site access protocols. Our project managers are always available for questions.',
  },
  {
    id: 'faq-6',
    question: 'What happens if I want to make changes mid-project?',
    answer: 'We understand that visions evolve. Minor changes can often be accommodated at no extra cost. Significant changes may affect timeline and budget, which we\'ll discuss transparently before proceeding. We provide change order documentation for all modifications.',
  },
  {
    id: 'faq-7',
    question: 'How does the free consultation work?',
    answer: 'Our free consultation includes an in-home visit where we assess your space, discuss your vision and budget, and provide preliminary recommendations. We\'ll take measurements and photos, then follow up with a detailed proposal within 3-5 business days. There\'s no obligation to proceed.',
  },
  {
    id: 'faq-8',
    question: 'What warranties do you offer?',
    answer: 'All projects include our craftsmanship warranty: 1 year for Essential tier, 3 years for Premium, and 5 years for Signature projects. We also honor manufacturer warranties on materials and fixtures. If any issues arise, we\'ll address them promptly at no additional cost.',
  },
];

export const processSteps: ProcessStep[] = [
  {
    id: 'step-1',
    number: 1,
    title: 'Free Consultation',
    description: 'We visit your home to understand your vision, assess the space, and discuss budget. You\'ll receive a detailed proposal within 3-5 business days with no obligation.',
    icon: 'MessageSquare',
  },
  {
    id: 'step-2',
    number: 2,
    title: 'Custom Design Plan',
    description: 'Our designers create 3D renderings and material selections tailored to your style. We refine the plan together until it\'s perfect, then finalize contracts and timelines.',
    icon: 'Pencil',
  },
  {
    id: 'step-3',
    number: 3,
    title: 'Expert Installation',
    description: 'Our certified craftsmen bring your vision to life with precision and care. We maintain a clean, safe worksite and provide daily progress updates with photos.',
    icon: 'Hammer',
  },
  {
    id: 'step-4',
    number: 4,
    title: 'Final Walkthrough',
    description: 'We conduct a detailed inspection together, ensuring every detail meets our standards and your expectations. You don\'t pay the final balance until you\'re 100% satisfied.',
    icon: 'CheckCircle',
  },
];

export const blogPosts: BlogPost[] = [
  {
    id: 'blog-1',
    title: '2026 Flooring Trends: What Canadian Homeowners Are Choosing',
    category: 'Flooring',
    excerpt: 'Discover the hottest flooring trends for 2026, from wide-plank hardwood to luxury vinyl innovations. Learn which options offer the best value and durability for Canadian climates.',
    thumbnail: IMAGES.FLOORING_4,
    author: 'Jessica Martinez',
    date: '2026-04-15',
    readTime: '8 min',
    content: `
      <h2>The Evolution of Flooring in 2026</h2>
      <p>The flooring industry has undergone remarkable transformation in recent years, driven by advances in manufacturing technology and changing homeowner priorities. In 2026, Canadian homeowners are prioritizing durability, sustainability, and aesthetic versatility more than ever before.</p>
      
      <h3>1. Wide-Plank Hardwood: Timeless Elegance</h3>
      <p>Wide-plank hardwood flooring (7-10 inches wide) continues to dominate luxury renovations. These expansive planks showcase the natural beauty of wood grain and make rooms feel larger. Popular species include white oak, walnut, and maple, with matte and natural oil finishes replacing glossy polyurethane.</p>
      <p><strong>Why it works in Canada:</strong> Engineered wide-plank options handle temperature fluctuations better than solid wood, making them ideal for our climate extremes. The wider planks also mean fewer seams, creating a more seamless, upscale appearance.</p>
      
      <h3>2. Luxury Vinyl Plank (LVP): The Smart Choice</h3>
      <p>LVP technology has reached new heights in 2026. Today's luxury vinyl planks are virtually indistinguishable from real hardwood, with realistic textures, varied plank widths, and authentic color variations. The latest rigid core LVP products are 100% waterproof and incredibly durable.</p>
      <p><strong>Best applications:</strong> Basements, bathrooms, kitchens, and homes with pets or children. LVP offers the look of hardwood at a fraction of the cost, with superior moisture resistance and easier maintenance.</p>
      
      <h3>3. Herringbone and Chevron Patterns</h3>
      <p>Geometric patterns are making a major comeback. Herringbone (planks meet at 90-degree angles) and chevron (planks meet at 45-degree angles) installations add visual interest and a sense of movement to floors. These patterns work beautifully with both hardwood and LVP.</p>
      <p><strong>Design tip:</strong> Use herringbone in smaller spaces like hallways or powder rooms to create impact. In larger areas, the pattern adds sophistication without overwhelming the space.</p>
      
      <h3>4. Sustainable and Reclaimed Materials</h3>
      <p>Eco-conscious homeowners are choosing reclaimed wood, bamboo, and cork flooring. These materials offer unique character while reducing environmental impact. Reclaimed barn wood, in particular, brings warmth and history to modern interiors.</p>
      <p><strong>Sustainability note:</strong> Look for FSC-certified wood and low-VOC finishes. Many manufacturers now offer carbon-neutral flooring options with transparent supply chains.</p>
      
      <h3>5. Matte and Natural Finishes</h3>
      <p>The era of high-gloss floors is over. Matte, satin, and natural oil finishes are preferred for their ability to hide scratches and their more authentic appearance. These finishes also feel better underfoot and require less maintenance.</p>
      
      <h3>Color Trends for 2026</h3>
      <p>Neutral tones remain dominant, but we're seeing a shift toward warmer hues. Honey-toned oak, medium walnut, and greige (gray-beige) are replacing the cool grays of previous years. These warmer tones create inviting, cozy spaces that feel less sterile.</p>
      
      <h3>Making the Right Choice for Your Home</h3>
      <p>When selecting flooring, consider your lifestyle, budget, and long-term plans. Hardwood adds resale value but requires more maintenance. LVP offers practicality and affordability. Consult with a flooring specialist to explore samples in your home's lighting—colors and textures can look dramatically different in various environments.</p>
      
      <p><strong>Final thought:</strong> The best flooring choice is one that balances your aesthetic preferences with practical needs. In 2026, you don't have to compromise—modern materials offer both beauty and performance.</p>
    `,
  },
  {
    id: 'blog-2',
    title: 'The Ultimate Guide to Custom Kitchen Cabinets',
    category: 'Cabinets',
    excerpt: 'Everything you need to know about custom cabinetry, from wood species selection to hardware choices. Make informed decisions for your kitchen renovation.',
    thumbnail: IMAGES.CABINETS_7,
    author: 'David Park',
    date: '2026-04-08',
    readTime: '10 min',
    content: `
      <h2>Why Custom Cabinets Are Worth the Investment</h2>
      <p>Custom kitchen cabinets are the cornerstone of a successful kitchen renovation. Unlike stock or semi-custom options, fully custom cabinetry is built to your exact specifications, maximizing every inch of space and reflecting your personal style. While the investment is higher, the return in functionality, durability, and home value is substantial.</p>
      
      <h3>Understanding Cabinet Construction</h3>
      <p><strong>Frame vs. Frameless:</strong> Framed cabinets (traditional American style) have a face frame that adds strength and a classic look. Frameless cabinets (European style) offer more interior space and a sleek, modern appearance. Your choice depends on aesthetic preference and storage needs.</p>
      <p><strong>Box construction:</strong> Quality cabinets use 3/4-inch plywood boxes, not particleboard. Plywood is stronger, more moisture-resistant, and holds screws better over time. Dovetail or dowel joints indicate superior craftsmanship.</p>
      
      <h3>Wood Species: Choosing the Right Material</h3>
      <p><strong>Maple:</strong> The most popular choice for painted cabinets due to its smooth, tight grain. Extremely durable and takes paint beautifully. Ideal for contemporary and transitional kitchens.</p>
      <p><strong>Oak:</strong> A classic choice with prominent grain patterns. Red oak has warm tones; white oak is more neutral. Perfect for traditional and farmhouse styles. Very durable and ages gracefully.</p>
      <p><strong>Cherry:</strong> A premium hardwood that darkens beautifully over time, developing a rich patina. Smooth grain and natural luster make it ideal for formal, elegant kitchens. More expensive but worth it for luxury renovations.</p>
      <p><strong>Walnut:</strong> Dark, rich tones with striking grain patterns. A statement wood that works in modern and mid-century designs. Softer than maple or oak, so requires more care.</p>
      
      <h3>Door Styles and Their Impact</h3>
      <p><strong>Shaker:</strong> The most versatile and timeless style. Clean lines with a recessed center panel work in virtually any design aesthetic. Available in painted or stained finishes.</p>
      <p><strong>Slab/Flat-panel:</strong> Ultra-modern with no ornamentation. Creates a sleek, minimalist look. Often used in contemporary and Scandinavian-inspired kitchens.</p>
      <p><strong>Raised panel:</strong> Traditional style with a raised center panel. Adds depth and formality. Best suited for classic and transitional designs.</p>
      <p><strong>Glass-front:</strong> Upper cabinets with glass inserts create visual interest and display opportunities. Use for china, glassware, or decorative items. Can make small kitchens feel more open.</p>
      
      <h3>Hardware: The Jewelry of Your Kitchen</h3>
      <p>Cabinet hardware is where personality shines. In 2026, we're seeing a mix of finishes—brass and black, chrome and gold—used together for visual interest. Larger pulls (5-6 inches) on drawers and simple knobs on doors create a balanced look.</p>
      <p><strong>Trending finishes:</strong> Unlacquered brass (develops patina over time), matte black, brushed nickel, and polished chrome. Avoid trendy finishes that may date your kitchen quickly.</p>
      
      <h3>Maximizing Storage with Smart Features</h3>
      <p><strong>Pull-out organizers:</strong> Spice racks, trash bins, and cutting board storage that slide out for easy access. These maximize deep cabinet space.</p>
      <p><strong>Soft-close mechanisms:</strong> A must-have feature that prevents slamming and extends cabinet life. Adds a luxury feel to everyday use.</p>
      <p><strong>Corner solutions:</strong> Lazy Susans, magic corners, or pull-out systems make corner cabinets functional instead of wasted space.</p>
      <p><strong>Drawer dividers:</strong> Custom dividers for utensils, spices, and small items keep everything organized and accessible.</p>
      
      <h3>Finish Options: Paint vs. Stain</h3>
      <p><strong>Painted cabinets:</strong> Offer a clean, contemporary look and hide wood grain. White, navy, and sage green are popular in 2026. Require more maintenance as paint can chip, but are easier to refresh.</p>
      <p><strong>Stained cabinets:</strong> Showcase natural wood beauty and are more forgiving of wear. Require less maintenance and develop character over time. Medium to dark stains are trending.</p>
      <p><strong>Two-tone kitchens:</strong> Combining painted uppers with stained lowers (or vice versa) adds visual interest and can make small kitchens feel larger.</p>
      
      <h3>Budget Considerations</h3>
      <p>Custom cabinets typically range from $500-$1,500 per linear foot, depending on wood species, finish, and features. While this is a significant investment, quality cabinets last 30+ years and dramatically increase home value. Allocate 30-40% of your kitchen renovation budget to cabinetry.</p>
      
      <h3>Working with a Cabinet Designer</h3>
      <p>A professional designer will create 3D renderings, help you maximize storage, and ensure proper proportions. They'll also coordinate with your contractor to ensure cabinets fit perfectly with appliances, plumbing, and electrical. This expertise is invaluable and often included in the cabinet cost.</p>
      
      <p><strong>Final advice:</strong> Don't rush the cabinet selection process. Visit showrooms, touch samples, and see completed projects. The right cabinets will serve your family for decades—choose wisely.</p>
    `,
  },
  {
    id: 'blog-3',
    title: 'Budgeting for Your Home Renovation: A Realistic Guide',
    category: 'Planning',
    excerpt: 'Learn how to create a realistic renovation budget, avoid common cost overruns, and get the most value from your investment. Expert tips from 10+ years of projects.',
    thumbnail: IMAGES.RENOVATION_7,
    author: 'Michael Chen',
    date: '2026-03-28',
    readTime: '9 min',
    content: `
      <h2>The Truth About Renovation Costs</h2>
      <p>After managing hundreds of renovation projects across Canada, we've learned that the biggest source of homeowner stress isn't the construction itself—it's budget surprises. This guide will help you create a realistic budget and avoid the common pitfalls that lead to cost overruns.</p>
      
      <h3>The 20% Rule: Your Safety Net</h3>
      <p>The golden rule of renovation budgeting: add 20% to your estimated costs for contingencies. This isn't pessimism—it's realism. Hidden issues (old wiring, plumbing problems, structural surprises) are discovered in 80% of renovation projects. Having a contingency fund means these discoveries don't derail your project.</p>
      
      <h3>Breaking Down Renovation Costs</h3>
      <p><strong>Kitchen renovations ($25,000-$75,000):</strong></p>
      <ul>
        <li>Cabinets: 30-40% of budget</li>
        <li>Countertops: 10-15%</li>
        <li>Appliances: 15-20%</li>
        <li>Labor: 20-25%</li>
        <li>Flooring, lighting, plumbing: 15-20%</li>
      </ul>
      
      <p><strong>Bathroom renovations ($15,000-$40,000):</strong></p>
      <ul>
        <li>Fixtures and fittings: 25-30%</li>
        <li>Tile and flooring: 20-25%</li>
        <li>Vanity and countertop: 15-20%</li>
        <li>Labor: 25-30%</li>
        <li>Plumbing and electrical: 10-15%</li>
      </ul>
      
      <h3>Where to Splurge vs. Save</h3>
      <p><strong>Splurge on:</strong></p>
      <ul>
        <li><strong>Structural work:</strong> Foundation, framing, and load-bearing modifications. Cutting corners here is dangerous and costly to fix later.</li>
        <li><strong>Plumbing and electrical:</strong> Use licensed professionals and quality materials. These systems are hidden but critical.</li>
        <li><strong>Cabinets:</strong> Quality cabinetry lasts 30+ years. This is not the place to save money.</li>
        <li><strong>Flooring in high-traffic areas:</strong> Invest in durable materials that will withstand daily wear.</li>
      </ul>
      
      <p><strong>Save on:</strong></p>
      <ul>
        <li><strong>Decorative elements:</strong> Light fixtures, hardware, and accessories can be upgraded later without major construction.</li>
        <li><strong>Paint:</strong> A fresh coat of paint is inexpensive and high-impact. Don't overspend on premium brands for walls.</li>
        <li><strong>Countertop edges:</strong> Simple edges look clean and cost less than elaborate profiles.</li>
        <li><strong>Tile patterns:</strong> Straight-lay tile installation costs less than diagonal or herringbone patterns.</li>
      </ul>
      
      <h3>Hidden Costs to Anticipate</h3>
      <p><strong>Permits and inspections:</strong> Budget $500-$2,000 depending on project scope. Required for structural, electrical, and plumbing work.</p>
      <p><strong>Temporary living arrangements:</strong> If your kitchen or bathroom is unusable for weeks, factor in eating out or alternative accommodations.</p>
      <p><strong>Storage and moving:</strong> Furniture and belongings may need temporary storage during construction.</p>
      <p><strong>Landscaping repair:</strong> Construction equipment can damage lawns and gardens. Budget for restoration.</p>
      <p><strong>Cleaning:</strong> Post-construction cleaning is extensive. Professional services cost $300-$800 but are worth it.</p>
      
      <h3>Financing Your Renovation</h3>
      <p><strong>Home equity line of credit (HELOC):</strong> Typically offers the lowest interest rates. You borrow against your home's equity and only pay interest on what you use.</p>
      <p><strong>Personal loans:</strong> Fixed rates and terms make budgeting easier. Good for smaller projects ($10,000-$50,000).</p>
      <p><strong>Credit cards:</strong> Only for small projects or if you can pay off quickly. Interest rates are high.</p>
      <p><strong>Refinancing:</strong> If rates are favorable, refinancing your mortgage to access equity can provide large sums at low rates.</p>
      
      <h3>Getting Accurate Quotes</h3>
      <p>Get at least three detailed quotes from licensed contractors. Beware of quotes that are significantly lower than others—they often indicate missing items or lower-quality work. A detailed quote should include:</p>
      <ul>
        <li>Itemized material costs with specific brands/models</li>
        <li>Labor costs broken down by trade</li>
        <li>Timeline with milestones</li>
        <li>Payment schedule</li>
        <li>Warranty information</li>
        <li>What's NOT included (important!)</li>
      </ul>
      
      <h3>Maximizing ROI: What Adds Value</h3>
      <p><strong>Kitchen renovations:</strong> Recoup 60-80% of costs. Focus on quality cabinets, functional layout, and timeless design.</p>
      <p><strong>Bathroom additions:</strong> Adding a bathroom (especially to a one-bath home) can recoup 80-100% of costs.</p>
      <p><strong>Flooring:</strong> Hardwood floors add value and appeal. Recoup 70-80% of costs.</p>
      <p><strong>Energy-efficient upgrades:</strong> New windows, insulation, and HVAC systems appeal to buyers and may qualify for rebates.</p>
      
      <h3>Red Flags: When to Walk Away</h3>
      <ul>
        <li>Contractor requires large upfront payment (>30%)</li>
        <li>No written contract or vague terms</li>
        <li>Pressure to make quick decisions</li>
        <li>Unlicensed or uninsured</li>
        <li>Poor communication or unprofessional behavior</li>
        <li>No references or portfolio</li>
      </ul>
      
      <h3>Creating Your Budget Spreadsheet</h3>
      <p>Use a detailed spreadsheet to track every expense. Categories should include: demolition, framing, electrical, plumbing, HVAC, insulation, drywall, flooring, cabinetry, countertops, fixtures, appliances, painting, and contingency. Update it weekly as the project progresses.</p>
      
      <p><strong>Final wisdom:</strong> A realistic budget, thorough planning, and the right contractor make all the difference. Don't let budget constraints force you into decisions you'll regret. Sometimes it's better to wait and save more than to compromise on quality.</p>
    `,
  },
];
